<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import DreamaLoginLogo from '@/components/dreama-login-logo'
import SearchBeforePanel from '@/components/search-before-panel'
import SearchInputBar from '@/components/search-input-bar'
import SearchResultsPanel from '@/components/search-results-panel'
import { SEARCH_RESULT_TABS, useSearchPage } from '@/composables/useSearchPage'
import { useThemeStore } from '@/stores/theme'
import { useUserStore, type UserInfo } from '@/stores/user'
import type { SearchCartoonItem, SearchTopicItem, SearchUserItem } from '@/api/search'
import type { UserWorkItem } from '@/api/home/type'
import { buildCartoonUrl } from '@/utils/distribute/navigation'
import { openLegacyPlayerUrl } from '@/utils/legacy-player'
import { loginWithBlankClose } from '@/utils/login'
import { buildSummaryContentViewUrl } from '@/utils/profile/content-card'
import type { SearchPriorityUserResult } from '@/composables/useSearchPage'

defineOptions({
  name: 'SearchPage',
})

const route = useRoute()
const router = useRouter()
const themeStore = useThemeStore()
const userStore = useUserStore()
const search = useSearchPage()
const isLoggedIn = computed(() => userStore.isLoggedIn())
const visibleRecentSearches = computed(() => (isLoggedIn.value ? search.recentSearches.value : []))

function getRouteQueryKeyword() {
  return typeof route.query.q === 'string' ? route.query.q.trim() : ''
}

async function setRouteQueryKeyword(keyword: string) {
  const nextQuery = { ...route.query }

  if (keyword) {
    nextQuery.q = keyword
  } else {
    delete nextQuery.q
  }

  await router.replace({
    query: nextQuery,
  })
}

async function handleSearch(keyword: string) {
  const normalized = keyword.trim()

  search.setQuery(normalized)

  if (!normalized) {
    search.clearSubmittedSearch()
    await setRouteQueryKeyword('')
    return
  }

  if (!(await ensureLoggedIn())) {
    return
  }

  await setRouteQueryKeyword(normalized)
  await search.submitSearch(normalized)
}

async function handleBack() {
  search.clearSubmittedSearch()
  await setRouteQueryKeyword('')
}

async function retrySubmittedSearch() {
  await search.submitSearch(search.submittedQuery.value)
}

async function openWork(item: UserWorkItem) {
  if (!(await ensureLoggedIn())) {
    return
  }

  const url = buildSummaryContentViewUrl(item, themeStore.currentTheme)

  if (url) {
    openLegacyPlayerUrl(url)
  }
}

async function openAuthor(item: UserWorkItem) {
  if (!(await ensureLoggedIn())) {
    return
  }

  const uid = item.user?.uid

  if (uid) {
    void router.push(`/${encodeURIComponent(uid)}`)
  }
}

async function openCartoon(item: SearchCartoonItem) {
  if (!(await ensureLoggedIn())) {
    return
  }

  const url = buildCartoonUrl(item)

  if (url) {
    void router.push(url)
  }
}

async function openUser(item: SearchUserItem | SearchPriorityUserResult) {
  if (!(await ensureLoggedIn())) {
    return
  }

  if (item.uid) {
    void router.push(`/${encodeURIComponent(item.uid)}`)
  }
}

async function openTopic(item: SearchTopicItem) {
  await handleSearch(item.name)
}

async function ensureLoggedIn() {
  if (isLoggedIn.value) {
    return true
  }

  const userInfo = await loginWithBlankClose({
    logo: DreamaLoginLogo,
  })

  if (!userInfo) {
    return false
  }

  userStore.setUserInfo(userInfo as unknown as UserInfo)
  return true
}

onMounted(() => {
  void search.loadLandingData()

  const keyword = getRouteQueryKeyword()

  if (keyword) {
    search.setQuery(keyword)

    if (isLoggedIn.value) {
      void search.submitSearch(keyword)
    }
  }
})
</script>

<template>
  <section class="search-page">
    <div class="search-page__inner">
      <header class="search-page__header" :class="{ 'is-results': search.hasSubmittedQuery.value }">
        <div class="search-page__header-inner">
          <SearchInputBar
            v-model="search.query.value"
            :searched="search.hasSubmittedQuery.value"
            :autofocus="!search.hasSubmittedQuery.value"
            @submit="handleSearch"
            @back="handleBack"
          />
        </div>
      </header>

      <Transition name="search-panel" mode="out-in" appear>
        <SearchBeforePanel
          v-if="!search.hasSubmittedQuery.value"
          key="search-before"
          :recent-searches="visibleRecentSearches"
          :hot-words="search.hotWords.value"
          :recommendations="search.recommendations.value"
          :loading="search.loadingLanding.value"
          :loading-hot-words="search.loadingHotWords.value"
          :error="search.landingError.value"
          @search="handleSearch"
          @remove-recent="search.removeRecentSearch"
          @clear-recent="search.clearRecentSearches"
          @refresh-hot-words="search.loadHotWords"
          @retry="search.loadLandingData"
          @work-click="openWork"
          @author-click="openAuthor"
        />

        <SearchResultsPanel
          v-else
          key="search-results"
          :active-tab="search.activeTab.value"
          :tabs="SEARCH_RESULT_TABS"
          :query="search.submittedQuery.value"
          :result-counts="search.resultCounts.value"
          :cartoon-results="search.cartoonResults"
          :work-results="search.workResults"
          :user-results="search.userResults"
          :topic-results="search.topicResults"
          :priority-users="search.priorityUsers.value"
          :loading="search.isSearching.value"
          :loading-priority-users="search.loadingPriorityUsers.value"
          :priority-users-error="search.priorityUsersError.value"
          :has-any-results="search.hasAnyResults.value"
          :can-load-more="search.canLoadMoreActiveTab.value"
          @update:active-tab="search.setActiveTab"
          @load-more="search.loadMoreActiveTab"
          @retry="retrySubmittedSearch"
          @cartoon-click="openCartoon"
          @work-click="openWork"
          @author-click="openAuthor"
          @user-click="openUser"
          @topic-click="openTopic"
        />
      </Transition>
    </div>
  </section>
</template>

<style scoped>
.search-page {
  min-height: 100%;
  color: var(--app-text-main);
}

.search-page__inner {
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 0 56px;
}

.search-page__header {
  position: sticky;
  top: 0;
  z-index: 4;
  display: flex;
  width: 100%;
  flex-direction: column;
  margin: 0 0 16px;
  padding: 0;
  background: var(--app-bg);
}

.search-page__header-inner {
  width: min(100%, 640px);
  margin: 0 auto;
  padding: 0 16px;
}

.search-page__header.is-results {
  margin-bottom: 0;
}

.search-panel-enter-active,
.search-panel-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.22s var(--app-interaction-ease);
}

.search-panel-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.search-panel-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (max-width: 768px) {
  .search-page__inner {
    padding: 0 0 42px;
  }

  .search-page__header {
    padding: 0;
  }

  .search-page__header-inner {
    padding: 0 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .search-panel-enter-active,
  .search-panel-leave-active {
    transition: none;
  }

  .search-panel-enter-from,
  .search-panel-leave-to {
    transform: none;
  }
}
</style>
